import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Typography,
  Card,
  Grid,
  Divider,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControlLabel,
  Checkbox as MuiCheckbox,
  Radio,
  Alert,
  Paper,
  Collapse,
  Container,
} from '@mui/material';
import Chip from '@mui/material/Chip';
import CheckIcon from '@mui/icons-material/Check';
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined';
import TrackChangesOutlinedIcon from '@mui/icons-material/TrackChangesOutlined';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import HomeWorkOutlinedIcon from '@mui/icons-material/HomeWorkOutlined';
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import { BASE_URL } from '../../../Api/ApiUrls';
import GuestLoginModal from '../../../../Components/GuestLoginModal';
import { AuthContext } from './../../../AuthContext/AuthContext';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
// =========================================================
// THEME TOKENS
// =========================================================
const GOLD = '#A8752D';
const LIGHT_GOLD = '#FBF5ED';
const BLACK = '#111111';

// =========================================================
// USER TYPE -> LABEL / FEATURES / ICON
// =========================================================
const userTypeMappings = {
  seller: {
    label: 'Individual Owner/Builder',
    features: ['no_of_properties', 'no_of_liked_data', 'matching_enquiry'],
    icon: <PersonOutlineOutlinedIcon />,
  },
  agent: {
    label: 'Agent',
    features: ['no_of_properties', 'no_of_liked_data', 'matching_enquiry'],
    icon: <PersonAddAltOutlinedIcon />,
  },
  landlord: {
    label: 'Landlord',
    features: ['no_of_properties', 'no_of_liked_data', 'matching_enquiry'],
    icon: <HomeWorkOutlinedIcon />,
  },
  bankauction: {
    label: 'Bank Auction',
    features: ['no_of_properties'],
    icon: <GavelOutlinedIcon />,
  },
};

// ✅ Plan order definition
const PLAN_ORDER = ['Free', '1 Months', '3 Months', '6 Months', '12 Months', 'Lifetime'];

const BENEFITS = [
  { icon: <SecurityOutlinedIcon />, title: 'Verified', subtitle: 'Listings' },
  { icon: <TrackChangesOutlinedIcon />, title: 'Better', subtitle: 'Visibility' },
  { icon: <ForumOutlinedIcon />, title: 'More Buyer', subtitle: 'Inquiries' },
  { icon: <TrendingUpOutlinedIcon />, title: 'Grow Your', subtitle: 'Business' },
];

// =========================================================
// USER TYPE CARD ("I am a:" grid) - matches image layout
// =========================================================
const UserTypeCard = ({ userType, mapping, active, onClick }) => {
  return (
    <Card
      elevation={0}
      onClick={onClick}
      sx={{
        width: '100%',
        height: { xs: 35, sm: 64, md: 68 },
        borderRadius: { xs: '10px', sm: '12px' },
        border: active ? `1px solid ${GOLD}` : '1px solid #E5E2DF',
        background: active ? LIGHT_GOLD : '#FFFFFF',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: { xs: 1.5, sm: 2 },
        cursor: 'pointer',
        boxSizing: 'border-box',
        transition: 'background 0.2s ease, border 0.2s ease',
        '&:hover': { background: active ? LIGHT_GOLD : '#FAFAFA' },
      }}
    >
      {/* Left: icon + label */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 }, minWidth: 0 }}>
        <Box
          sx={{
            color: '#896333',
            display: 'flex',
            alignItems: 'center',
            flexShrink: 0,
            '& svg': { fontSize: { xs: 20, sm: 24, md: 26 } },
          }}
        >
          {mapping.icon}
        </Box>

        <Typography
          sx={{
            fontSize: { xs: 12, sm: 14, md: 15 },
            fontWeight: 600,
            color: '#222',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {mapping.label}
        </Typography>
      </Box>

      {/* Right: square checkbox */}
      <MuiCheckbox
        checked={active}
        onChange={onClick}
        size="small"
        sx={{
          p: 0,
          flexShrink: 0,
          color: '#999',
          '&.Mui-checked': { color: GOLD },
        }}
      />
    </Card>
  );
};

// =========================================================
// ACCESS TABLE ROW COMPONENT
// =========================================================
const AccessRow = ({ name, values }) => {
  return (
    <TableRow>
      <TableCell
        sx={{
          fontSize: { xs: '8px', sm: '10px', md: '11px' },
          fontWeight: 600,
          color: '#333',
          p: { xs: '4px 6px', sm: '6px 8px' },
          border: '1px solid #ddd',
          background: '#FAFAFA',
        }}
      >
        {name}
      </TableCell>
      {values.map((value, index) => (
        <TableCell
          key={index}
          align="center"
          sx={{
            fontSize: { xs: '7px', sm: '9px', md: '10px' },
            fontWeight: 500,
            p: { xs: '4px 2px', sm: '6px 4px' },
            border: '1px solid #ddd',
            color: value === 'YES' ? '#2E7D32' : '#D32F2F',
          }}
        >
          {value}
        </TableCell>
      ))}
    </TableRow>
  );
};

// =========================================================
// MAIN PLANS / ADDON PLANS PILL TAB
// =========================================================
const PlanTab = ({ active, children, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      width: '50%',
      height: '100%',
      borderRadius: '28px',
      background: active ? BLACK : 'transparent',
      color: active ? '#FFFFFF' : '#555555',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
    }}
  >
    <Typography sx={{ fontSize: { xs: 12, sm: 14, md: 16 }, fontWeight: 600, whiteSpace: 'nowrap' }}>
      {children}
    </Typography>
  </Box>
);

// =========================================================
// PLAN FEATURE ROW
// =========================================================
const PlanFeature = ({ text }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: { xs: 0.6, sm: 0.8 }, mb: { xs: 0.9, sm: 1.2 }, minWidth: 0 }}>
    <Box
      sx={{
        width: { xs: 15, sm: 17 },
        height: { xs: 15, sm: 17 },
        borderRadius: '50%',
        border: '1.5px solid #8E795D',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        mt: '2px',
      }}
    >
      <CheckIcon sx={{ fontSize: { xs: 9, sm: 11 }, color: '#8E795D' }} />
    </Box>
    <Typography sx={{ fontSize: { xs: 10.5, sm: 12 }, color: '#333333', lineHeight: 1.3 }}>{text}</Typography>
  </Box>
);

// =========================================================
// PLAN CARD (real data, gold/black skin)
// =========================================================
const PlanCard = ({
  plan,
  isSelected,
  onSelect,
  userType,
  isAddon = false,
  iosPlans = [],
  isIOSApp = false,
}) => {
  const mapping = userTypeMappings[userType] || {};
  const isFreePlan = plan.plan_name === 'Free';
  const isPopular = !!plan.isPopular;

  const getApplePrice = () => {
    if (!iosPlans || iosPlans.length === 0) return null;
    const match = iosPlans.find((p) => p.productId === plan.apple_product_id);
    return match?.price ?? null;
  };

  const applePrice = isIOSApp ? getApplePrice() : null;
  const totalAmount = applePrice ? parseFloat(String(applePrice).replace(/[^\d.]/g, '')) : null;
  const basePrice = totalAmount ? (totalAmount / 1.18).toFixed(2) : null;
  const hasDiscount = !isFreePlan && plan.actual_price && plan.actual_price !== plan.charges;
  const discountPct = hasDiscount ? Math.round((1 - plan.charges / plan.actual_price) * 100) : null;
  return (
    <Card
      elevation={0}
      onClick={onSelect}
      sx={{
        flex: { xs: '0 0 200px', sm: '0 0 235px', md: '0 0 245px' },
        width: { xs: 200, sm: 235, md: 245 },
        minHeight: { xs: 300, sm: 470, md: 480 },
        borderRadius: { xs: '14px', sm: '18px' },
        border: isSelected ? `2px solid ${GOLD}` : isPopular ? `2px solid ${GOLD}` : '1px solid #E4E0DC',
        background: isSelected ? LIGHT_GOLD : '#FFFFFF',
        overflow: 'hidden',
        boxSizing: 'border-box',
        flexShrink: 0,
        cursor: 'pointer',
        transition: 'all 0.25s ease',
        '&:hover': { boxShadow: '0 6px 20px rgba(0,0,0,0.08)' },
      }}
    >
      {isPopular && (
        <Box
          sx={{
            height: { xs: 30, sm: 38 },
            background: GOLD,
            color: '#FFFFFF',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.5,
          }}
        >
          <Box sx={{ width: { xs: 13, sm: 20 }, height: 1, background: '#FFFFFF' }} />
          <Typography sx={{ fontSize: { xs: 12, sm: 16 }, lineHeight: 1 }}>★</Typography>
          <Typography sx={{ fontSize: { xs: 9, sm: 12, md: 13 }, fontWeight: 600, whiteSpace: 'nowrap' }}>
            Most Popular
          </Typography>
          <Box sx={{ width: { xs: 13, sm: 20 }, height: 1, background: '#FFFFFF' }} />
        </Box>
      )}

      <Box sx={{ px: { xs: 1.2, sm: 1.7, md: 2 }, py: { xs: 1.3, sm: 2 }, textAlign: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
          <Radio
            checked={isSelected}
            color={isAddon ? 'warning' : 'primary'}
            size="small"
            sx={{
              p: 0.3,
              color: isSelected ? GOLD : '#ccc',
              '&.Mui-checked': { color: GOLD },
            }}
          />
          <Typography
            sx={{ fontSize: { xs: 15, sm: 19, md: 20 }, fontWeight: 700 }}
          >
            {isAddon ? plan.plan_name || 'Addon Plan' : plan.plan_name}
          </Typography>
        </Box>

        <Box sx={{ width: { xs: 28, sm: 37 }, height: 2, background: '#AAAAAA', mx: 'auto', my: { xs: 0.8, sm: 1.4 } }} />

        {isFreePlan ? (
          <Typography sx={{ fontSize: { xs: 24, sm: 30, md: 32 }, fontWeight: 700, color: '#4caf50' }}>
            FREE
          </Typography>
        ) : (
          <>
            <Typography sx={{ fontSize: { xs: 24, sm: 30, md: 32 }, fontWeight: 700 }}>
              {basePrice ? `₹${basePrice}` : `₹${plan.charges}`}
            </Typography>
            {hasDiscount && (
              <Typography sx={{ fontSize: { xs: 11, sm: 15, md: 16 }, color: '#888888', textDecoration: 'line-through' }}>
                ₹{plan.actual_price}
              </Typography>
            )}
            {hasDiscount && (
              <Box
                sx={{
                  display: 'inline-flex',
                  background: '#F8F1E8',
                  color: '#9A6C36',
                  borderRadius: '7px',
                  px: { xs: 1, sm: 1.3 },
                  py: { xs: 0.4, sm: 0.6 },
                  mt: { xs: 0.7, sm: 1 },
                  fontSize: { xs: 10, sm: 13 },
                  fontWeight: 700,
                }}
              >
                {discountPct}% OFF
              </Box>
            )}
          </>
        )}

        <Divider sx={{ my: { xs: 1.5, sm: 2 }, borderColor: 'rgba(0,0,0,0.08)' }} />

        <Box sx={{ textAlign: 'left' }}>
          {mapping.features?.map((feature) => {
            const unlimitedKey = `${feature}_unlimited`;
            const isUnlimited = plan[unlimitedKey];
            const value = isUnlimited ? 'unlimited' : plan[feature];
            let displayText = '';

            if (feature === 'no_of_properties') {
              displayText = isUnlimited
                ? 'List an unlimited number of properties'
                : `List properties limit: ${value}`;
            } else if (feature === 'no_of_liked_data') {
              displayText = isUnlimited
                ? 'Access wishlist without limitations'
                : `Wishlist access limit: ${value}`;
            } else if (feature === 'matching_enquiry') {
              const inquiryText = userType === 'landlord' ? 'Tenant' : 'Buyer';
              displayText = isUnlimited
                ? `View unlimited ${inquiryText.toLowerCase()} inquiries`
                : `${inquiryText} inquiries access limit: ${value}`;
            }

            return <PlanFeature key={feature} text={displayText} />;
          })}
          <PlanFeature text="Property boost feature" />
        </Box>

        <Button
          fullWidth
          variant={isSelected ? 'contained' : 'outlined'}
          onClick={onSelect}
          startIcon={isSelected ? <CheckIcon /> : null}
          sx={{
            mt: { xs: 2, sm: 3.5 },
            height: { xs: 38, sm: 45, md: 47 },
            borderRadius: '25px',
            borderColor: GOLD,
            background: isSelected ? GOLD : '#FFFFFF',
            color: isSelected ? '#FFFFFF' : GOLD,
            fontSize: { xs: 12, sm: 15 },
            fontWeight: 700,
            textTransform: 'none',
            '&:hover': { borderColor: GOLD, background: isSelected ? GOLD : '#FFF7EE' },
          }}
        >
          {isSelected ? 'Selected' : isAddon ? 'Select Addon' : 'Select Plan'}
        </Button>
      </Box>
    </Card>
  );
};

// =========================================================
// MAIN COMPONENT
// =========================================================
const SubscriptionForm = ({
  selectedUserType,
  setSelectedUserType,
  selectedPlan,
  setSelectedPlan,
  selectedAddon,
  setSelectedAddon,
  plans,
  handlePaymentSubmit,
  subscriptions = [],
  iosPlans = [],
  userId,

}) => {
  const [activeTab, setActiveTab] = useState(0); // 0 = Main Plans, 1 = Addon Plans
  const [tableVisible, setTableVisible] = useState(true);
  const alertRef = React.useRef(null);
  const [addonPlans, setAddonPlans] = useState([]);
  const [loadingAddons, setLoadingAddons] = useState(false);
  const paymentRef = React.useRef(null);
  const [clickedPlan, setClickedPlan] = useState(null);
  const [autoPayEnabled] = useState(false);
  const [showGuestModal, setShowGuestModal] = useState(false);
  const [showTable, setShowTable] = useState(false);

  const isIOSApp =
    window.ReactNativeWebView &&
    window.ReactNativeWebView.postMessage &&
    navigator.userAgent.includes('iPhone');

  const handleIOSPurchase = (e) => {
    e.preventDefault();
    if (!selectedPlan) {
      alert('Please select a plan');
      return;
    }

    const productId = selectedPlan.apple_product_id;

    if (!productId) {
      alert('This plan is not available for iOS purchase yet');
      return;
    }

    window.ReactNativeWebView.postMessage(
      JSON.stringify({
        type: 'BUY_SUBSCRIPTION',
        productId: productId,
        user_id: userId,
        plan_name: selectedPlan.plan_name,
        user_type: selectedPlan.user_type,
        plan_id: selectedPlan.plan_id,
      }),
    );
  };

  const { isGuest } = useContext(AuthContext);

  const hasPaidPlans = plans.some((p) => p.plan_name !== 'Free');
  const allPlansAreFree = !hasPaidPlans;

  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (isGuest) {
      setShowGuestModal(true);
      return;
    }
    handlePaymentSubmit(e, autoPayEnabled);
  };

  const hasActiveMainPlan = subscriptions.some(
    (sub) => sub.user_type === userTypeMappings[selectedUserType]?.label && sub.status === true,
  );

  const hasExistingSubscription =
    activeTab === 0 && clickedPlan
      ? subscriptions.some(
        (sub) =>
          sub.user_type === clickedPlan.user_type &&
          sub.status === true &&
          (clickedPlan.plan_name === 'Free' || sub.plan_name !== 'Free'),
      )
      : false;

  useEffect(() => {
    if (hasExistingSubscription && alertRef.current) {
      alertRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [hasExistingSubscription]);

  const showPaymentSection =
    (selectedAddon && !loadingAddons) || (selectedPlan && activeTab === 0 && !hasExistingSubscription);

  const filteredAddonPlans = hasActiveMainPlan ? addonPlans : [];
  const handleUserTypeChange = (userType) => {
    setSelectedUserType(userType);
    setSelectedPlan(null);
    setSelectedAddon(null);
    setActiveTab(0);
    // No need to control showTable anymore
  };
  const handlePlanSelect = (plan) => {
    if (activeTab === 0) {
      setClickedPlan(plan);
      const hasConflict = subscriptions.some(
        (sub) =>
          sub.user_type === plan.user_type &&
          sub.status === true &&
          (plan.plan_name === 'Free' || sub.plan_name !== 'Free'),
      );
      if (hasConflict) {
        setSelectedPlan(null);
        setSelectedAddon(null);
      } else {
        setSelectedPlan(plan);
        setSelectedAddon(null);
      }
    } else {
      setSelectedAddon(plan);
      setSelectedPlan(null);
      setClickedPlan(null);
    }

    setTimeout(() => {
      paymentRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 100);
  };

  const getFilteredPlans = () => {
    if (!selectedUserType) return [];

    const userTypeLabel = userTypeMappings[selectedUserType].label;
    const paidPlansExist = plans.some((p) => p.plan_name !== 'Free');

    return plans
      .filter(
        (plan) =>
          plan.user_type === userTypeLabel &&
          plan.status === true &&
          (paidPlansExist ? plan.plan_name !== 'Free' : true),
      )
      .map((plan) => ({
        ...plan,
        displayName: `${plan.plan_name} ${userTypeLabel} Plan`,
      }))
      .sort((a, b) => {
        const indexA = PLAN_ORDER.indexOf(a.plan_name);
        const indexB = PLAN_ORDER.indexOf(b.plan_name);
        return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB);
      });
  };

  useEffect(() => {
    if (!selectedUserType) return;
    const fetchAddonPlans = async () => {
      setLoadingAddons(true);
      try {
        const response = await fetch(`${BASE_URL}/add-on/`);
        const data = await response.json();
        const userTypeLabel = userTypeMappings[selectedUserType].label;
        const filteredAddons = data.filter(
          (addon) => addon.user_type === userTypeLabel && addon.status === true,
        );
        setAddonPlans(filteredAddons);
      } catch (error) {
        console.error('Error fetching addon plans:', error);
        setAddonPlans([]);
      } finally {
        setLoadingAddons(false);
      }
    };
    fetchAddonPlans();
  }, [selectedUserType]);

  const totalWithoutGST = selectedAddon ? selectedAddon.charges : selectedPlan?.charges;
  const isSelectedFreePlan = selectedPlan?.plan_name === 'Free';
  const gst = totalWithoutGST ? Math.round(totalWithoutGST * 0.18) : 0;

  const visibleUserTypes = Object.keys(userTypeMappings).filter((userType) => {
    if (allPlansAreFree) return true;
    const label = userTypeMappings[userType].label;
    return plans.some(
      (plan) =>
        plan.user_type === label &&
        plan.status === true &&
        (plans.some((p) => p.plan_name !== 'Free') ? plan.plan_name !== 'Free' : true),
    );
  });

  return (
    <Container sx={{ py: { xs: 0.5, sm: 3, md: 4 }, flex: 1, display: 'flex', flexDirection: 'column', gap: 0, maxWidth: '1200px' }}>
      {/* =====================================================
            I AM A
        ===================================================== */}
      <Box sx={{ px: { xs: 0.2, sm: 1 } }}>
        <Typography sx={{ fontSize: { xs: 16, sm: 20, md: 23 }, fontWeight: 700, mb: { xs: 0.5, sm: 2 } }}>
          I am a:
        </Typography>

        {allPlansAreFree && (
          <Card
            sx={{
              mb: 2,
              p: { xs: 0.5, sm: 3 },
              borderRadius: '16px',
              textAlign: 'center',
              background: `linear-gradient(135deg, ${LIGHT_GOLD} 0%, #fff 100%)`,
              border: '1px solid #EFE1D2',
              boxShadow: `0 6px 20px rgba(168, 117, 45, 0.12)`,
            }}
          >
            <Typography sx={{ fontSize: { xs: '2rem', sm: '2.5rem' }, mb: 1 }}>🎉</Typography>
            <Typography sx={{ fontWeight: 700, fontSize: { xs: '1.1rem', sm: '1.3rem' }, color: GOLD, mb: 1 }}>
              All Plans Are FREE
            </Typography>
            <Typography sx={{ fontSize: { xs: '0.9rem', sm: '1rem' }, color: '#555', lineHeight: 1.6, px: { xs: 1, sm: 2 } }}>
              Enjoy full access at no cost for now. Paid plans with premium features will be available soon 🚀
            </Typography>
          </Card>
        )}
        <Box
          sx={{
            width: '100%',
            display: 'grid',
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            gap: { xs: 0.5, sm: 1.5, md: 2 },
          }}
        >
          {visibleUserTypes.map((userType) => (
            <UserTypeCard
              key={userType}
              userType={userType}
              mapping={userTypeMappings[userType]}
              active={selectedUserType === userType}
              onClick={() => handleUserTypeChange(userType)}
            />
          ))}
        </Box>
      </Box>

      {/* =====================================================
    USER TYPES & ACCESS RIGHTS TABLE - With Checkbox
===================================================== */}

      <Box
        sx={{
          width: "100%",
          px: {
            xs: 0.5,
            sm: 2,
            md: 3,
          },
          mt: {
            xs: 0.5,
            sm: 3,
            md: 4,
          },
          boxSizing: "border-box",
        }}
      >

        {/* Table with Collapse */}
        <Collapse in={!selectedUserType} timeout="auto" unmountOnExit>
          <Box>
            {/* TABLE TITLE */}
            <Box
              sx={{
                width: "100%",
                background: "#202733",
                color: "#fff",
                py: {
                  xs: 1,
                  sm: 1.2,
                  md: 1.5,
                },
                textAlign: "center",
                borderRadius: "8px 8px 0 0",
              }}
            >
              <Typography
                sx={{
                  fontSize: {
                    xs: 14,
                    sm: 16,
                    md: 18,
                  },
                  fontWeight: 700,
                }}
              >
                User Types & Access Rights to Listings
              </Typography>
            </Box>

            {/* TABLE */}
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{
                width: "100%",
                border: "1px solid #E2E2E2",
                borderTop: "none",
                borderRadius: "0 0 8px 8px",
                overflow: "hidden",
              }}
            >
              <Table
                size="small"
                sx={{
                  width: "100%",
                  tableLayout: "fixed",
                  borderCollapse: "collapse",
                  "& th, & td": {
                    borderRight: "1px solid #E2E2E2",
                    borderBottom: "1px solid #E2E2E2",
                  },
                  "& th:last-child, & td:last-child": {
                    borderRight: "none",
                  },
                  "& tr:last-child td": {
                    borderBottom: "none",
                  },
                }}
              >
                <TableHead>
                  <TableRow sx={{ background: "#FFFFFF" }}>
                    <TableCell
                      sx={{
                        width: "24%", // Matches screenshot proportion
                        fontSize: { xs: 11, sm: 13, md: 14 },
                        fontWeight: 700,
                        textAlign: "left",
                        color: "#8A6A3D",
                        py: { xs: 1, sm: 1.2 },
                        px: { xs: 0.8, sm: 1.2 },
                        border: "1px solid #E2E2E2",
                      }}
                    >
                      User Types
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        width: "23%", // Matches screenshot proportion
                        fontSize: { xs: 10, sm: 12, md: 13 },
                        fontWeight: 700,
                        color: "#8A6A3D",
                        py: { xs: 1, sm: 1.2 },
                        px: 0.5,
                        lineHeight: 1.2,
                        border: "1px solid #E2E2E2",
                      }}
                    >
                      Individual
                      <br />
                      owners/builders
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        width: "15%", // Matches screenshot proportion
                        fontSize: { xs: 11, sm: 13, md: 14 },
                        fontWeight: 700,
                        color: "#8A6A3D",
                        py: { xs: 1, sm: 1.2 },
                        px: 0.5,
                        border: "1px solid #E2E2E2",
                      }}
                    >
                      Agent
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        width: "19%", // Matches screenshot proportion
                        fontSize: { xs: 11, sm: 13, md: 14 },
                        fontWeight: 700,
                        color: "#8A6A3D",
                        py: { xs: 1, sm: 1.2 },
                        px: 0.5,
                        border: "1px solid #E2E2E2",
                      }}
                    >
                      Landlord
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        width: "19%", // Matches screenshot proportion
                        fontSize: { xs: 10, sm: 12, md: 13 },
                        fontWeight: 700,
                        color: "#8A6A3D",
                        py: { xs: 1, sm: 1.2 },
                        px: 0.5,
                        lineHeight: 1.2,
                        border: "1px solid #E2E2E2",
                      }}
                    >
                      Auction
                      <br />
                      properties
                    </TableCell>
                  </TableRow>
                </TableHead>

                {/* BODY */}
                <TableBody>
                  {/* Sale */}
                  <TableRow>
                    <TableCell
                      sx={{
                        fontSize: { xs: 11, sm: 13, md: 14 },
                        fontWeight: 600,
                        color: '#333',
                        py: { xs: 0.8, sm: 1 },
                        px: { xs: 0.8, sm: 1.2 },
                        border: '1px solid #E2E2E2',
                        background: '#FAFAFA',
                      }}
                    >
                      Sale
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        fontSize: { xs: 10, sm: 12, md: 13 },
                        fontWeight: 600,
                        color: '#2E7D32',
                        py: { xs: 0.8, sm: 1 },
                        px: 0.5,
                        border: '1px solid #E2E2E2',
                      }}
                    >
                      YES
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        fontSize: { xs: 10, sm: 12, md: 13 },
                        fontWeight: 600,
                        color: '#2E7D32',
                        py: { xs: 0.8, sm: 1 },
                        px: 0.5,
                        border: '1px solid #E2E2E2',
                      }}
                    >
                      YES
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        fontSize: { xs: 10, sm: 12, md: 13 },
                        fontWeight: 500,
                        color: '#C62828',
                        py: { xs: 0.8, sm: 1 },
                        px: 0.5,
                        border: '1px solid #E2E2E2',
                      }}
                    >
                      NO
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        fontSize: { xs: 10, sm: 12, md: 13 },
                        fontWeight: 500,
                        color: '#C62828',
                        py: { xs: 0.8, sm: 1 },
                        px: 0.5,
                        border: '1px solid #E2E2E2',
                      }}
                    >
                      NO
                    </TableCell>
                  </TableRow>

                  {/* Rent/Lease */}
                  <TableRow>
                    <TableCell
                      sx={{
                        fontSize: { xs: 11, sm: 13, md: 14 },
                        fontWeight: 600,
                        color: '#333',
                        py: { xs: 0.8, sm: 1 },
                        px: { xs: 0.8, sm: 1.2 },
                        border: '1px solid #E2E2E2',
                        background: '#FAFAFA',
                      }}
                    >
                      Rent/Lease
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        fontSize: { xs: 10, sm: 12, md: 13 },
                        fontWeight: 600,
                        color: '#2E7D32',
                        py: { xs: 0.8, sm: 1 },
                        px: 0.5,
                        border: '1px solid #E2E2E2',
                      }}
                    >
                      YES
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        fontSize: { xs: 10, sm: 12, md: 13 },
                        fontWeight: 600,
                        color: '#2E7D32',
                        py: { xs: 0.8, sm: 1 },
                        px: 0.5,
                        border: '1px solid #E2E2E2',
                      }}
                    >
                      YES
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        fontSize: { xs: 10, sm: 12, md: 13 },
                        fontWeight: 600,
                        color: '#2E7D32',
                        py: { xs: 0.8, sm: 1 },
                        px: 0.5,
                        border: '1px solid #E2E2E2',
                      }}
                    >
                      YES
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        fontSize: { xs: 10, sm: 12, md: 13 },
                        fontWeight: 500,
                        color: '#C62828',
                        py: { xs: 0.8, sm: 1 },
                        px: 0.5,
                        border: '1px solid #E2E2E2',
                      }}
                    >
                      NO
                    </TableCell>
                  </TableRow>

                  {/* JD/JV */}
                  <TableRow>
                    <TableCell
                      sx={{
                        fontSize: { xs: 11, sm: 13, md: 14 },
                        fontWeight: 600,
                        color: '#333',
                        py: { xs: 0.8, sm: 1 },
                        px: { xs: 0.8, sm: 1.2 },
                        border: '1px solid #E2E2E2',
                        background: '#FAFAFA',
                      }}
                    >
                      JD/JV
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        fontSize: { xs: 10, sm: 12, md: 13 },
                        fontWeight: 600,
                        color: '#2E7D32',
                        py: { xs: 0.8, sm: 1 },
                        px: 0.5,
                        border: '1px solid #E2E2E2',
                      }}
                    >
                      YES
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        fontSize: { xs: 10, sm: 12, md: 13 },
                        fontWeight: 600,
                        color: '#2E7D32',
                        py: { xs: 0.8, sm: 1 },
                        px: 0.5,
                        border: '1px solid #E2E2E2',
                      }}
                    >
                      YES
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        fontSize: { xs: 10, sm: 12, md: 13 },
                        fontWeight: 500,
                        color: '#C62828',
                        py: { xs: 0.8, sm: 1 },
                        px: 0.5,
                        border: '1px solid #E2E2E2',
                      }}
                    >
                      NO
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        fontSize: { xs: 10, sm: 12, md: 13 },
                        fontWeight: 500,
                        color: '#C62828',
                        py: { xs: 0.8, sm: 1 },
                        px: 0.5,
                        border: '1px solid #E2E2E2',
                      }}
                    >
                      NO
                    </TableCell>
                  </TableRow>

                  {/* Build to Suit */}
                  <TableRow>
                    <TableCell
                      sx={{
                        fontSize: { xs: 11, sm: 13, md: 14 },
                        fontWeight: 600,
                        color: '#333',
                        py: { xs: 0.8, sm: 1 },
                        px: { xs: 0.8, sm: 1.2 },
                        border: '1px solid #E2E2E2',
                        background: '#FAFAFA',
                      }}
                    >
                      Build to Suit
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        fontSize: { xs: 10, sm: 12, md: 13 },
                        fontWeight: 600,
                        color: '#2E7D32',
                        py: { xs: 0.8, sm: 1 },
                        px: 0.5,
                        border: '1px solid #E2E2E2',
                      }}
                    >
                      YES
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        fontSize: { xs: 10, sm: 12, md: 13 },
                        fontWeight: 600,
                        color: '#2E7D32',
                        py: { xs: 0.8, sm: 1 },
                        px: 0.5,
                        border: '1px solid #E2E2E2',
                      }}
                    >
                      YES
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        fontSize: { xs: 10, sm: 12, md: 13 },
                        fontWeight: 500,
                        color: '#C62828',
                        py: { xs: 0.8, sm: 1 },
                        px: 0.5,
                        border: '1px solid #E2E2E2',
                      }}
                    >
                      NO
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        fontSize: { xs: 10, sm: 12, md: 13 },
                        fontWeight: 500,
                        color: '#C62828',
                        py: { xs: 0.8, sm: 1 },
                        px: 0.5,
                        border: '1px solid #E2E2E2',
                      }}
                    >
                      NO
                    </TableCell>
                  </TableRow>

                  {/* Auction Properties */}
                  <TableRow>
                    <TableCell
                      sx={{
                        fontSize: { xs: 11, sm: 13, md: 14 },
                        fontWeight: 600,
                        color: '#333',
                        py: { xs: 0.8, sm: 1 },
                        px: { xs: 0.8, sm: 1.2 },
                        border: '1px solid #E2E2E2',
                        background: '#FAFAFA',
                      }}
                    >
                      Auction Properties
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        fontSize: { xs: 10, sm: 12, md: 13 },
                        fontWeight: 500,
                        color: '#C62828',
                        py: { xs: 0.8, sm: 1 },
                        px: 0.5,
                        border: '1px solid #E2E2E2',
                      }}
                    >
                      NO
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        fontSize: { xs: 10, sm: 12, md: 13 },
                        fontWeight: 500,
                        color: '#C62828',
                        py: { xs: 0.8, sm: 1 },
                        px: 0.5,
                        border: '1px solid #E2E2E2',
                      }}
                    >
                      NO
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        fontSize: { xs: 10, sm: 12, md: 13 },
                        fontWeight: 500,
                        color: '#C62828',
                        py: { xs: 0.8, sm: 1 },
                        px: 0.5,
                        border: '1px solid #E2E2E2',
                      }}
                    >
                      NO
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        fontSize: { xs: 10, sm: 12, md: 13 },
                        fontWeight: 600,
                        color: '#2E7D32',
                        py: { xs: 0.8, sm: 1 },
                        px: 0.5,
                        border: '1px solid #E2E2E2',
                      }}
                    >
                      YES
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Collapse>
      </Box>
      {/* =====================================================
            AVAILABLE {TYPE} PLANS
        ===================================================== */}
      {selectedUserType && (
        <>
          <Box sx={{ textAlign: 'center', mt: { xs: 3.5, sm: 4.5 }, px: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: { xs: 0.8, sm: 1.8 } }}>
              <Box sx={{ width: { xs: 28, sm: 48, md: 55 }, height: 2, background: '#B79568' }} />
              <Typography sx={{ fontSize: { xs: 16, sm: 22, md: 25 }, fontWeight: 700, whiteSpace: 'nowrap' }}>
                Available {userTypeMappings[selectedUserType].label} Plans
              </Typography>
              <Box sx={{ width: { xs: 28, sm: 48, md: 55 }, height: 2, background: '#B79568' }} />
            </Box>
            <Typography sx={{ fontSize: { xs: 11, sm: 14, md: 16 }, color: '#555555', mt: 0.4 }}>
              Choose the plan that works best for you
            </Typography>
          </Box>

          <Box
            sx={{
              width: { xs: '80%', sm: '60%', md: 440 },
              height: { xs: 42, sm: 48, md: 50 },
              mx: 'auto',
              mt: 1.8,
              mb: 2.5,
              display: 'flex',
              background: '#FAF7F3',
              borderRadius: '25px',
              border: '1px solid #E1D8CF',
              overflow: 'hidden',
            }}
          >
            <PlanTab
              active={activeTab === 0}
              onClick={() => {
                setActiveTab(0);
                setClickedPlan(null);
              }}
            >
              Main Plans
            </PlanTab>
            <PlanTab
              active={activeTab === 1}
              onClick={() => {
                setActiveTab(1);
                setClickedPlan(null);
              }}
            >
              Addon Plans
            </PlanTab>
          </Box>

          <Box
            sx={{
              width: '100%',
              display: 'flex',
              gap: { xs: 1.2, sm: 2, md: 2 },
              overflowX: { xs: 'auto', md: getFilteredPlans().length > 4 ? 'auto' : 'visible' },
              justifyContent: { xs: 'flex-start', md: 'center' },
              px: { xs: 0.5, sm: 1 },
              pb: 1.5,
              boxSizing: 'border-box',
              '&::-webkit-scrollbar': { display: 'none' },
              scrollbarWidth: 'none',
            }}
          >
            {activeTab === 0 ? (
              getFilteredPlans().map((plan) => (
                <PlanCard
                  key={plan.plan_id}
                  plan={plan}
                  isSelected={selectedPlan?.plan_id === plan.plan_id}
                  onSelect={() => handlePlanSelect(plan)}
                  userType={selectedUserType}
                  iosPlans={iosPlans}
                  isIOSApp={isIOSApp}
                />
              ))
            ) : loadingAddons ? (
              <Typography>Loading addon plans...</Typography>
            ) : !hasActiveMainPlan ? (
              <Alert severity="info" sx={{ width: '100%', my: 2 }}>
                You need an active {userTypeMappings[selectedUserType]?.label} plan to purchase addons
              </Alert>
            ) : filteredAddonPlans.length > 0 ? (
              filteredAddonPlans.map((plan) => (
                <PlanCard
                  key={plan.addOn_id}
                  plan={plan}
                  isSelected={selectedAddon?.addOn_id === plan.addOn_id}
                  onSelect={() => handlePlanSelect(plan)}
                  userType={selectedUserType}
                  isAddon
                  iosPlans={iosPlans}
                  isIOSApp={isIOSApp}
                />
              ))
            ) : (
              <Typography>No addon plans available for this user type</Typography>
            )}
          </Box>

          {activeTab === 0 && hasExistingSubscription && (
            <Alert severity="warning" sx={{ mt: 2, mb: 2 }} ref={alertRef}>
              You already have an Active {clickedPlan?.user_type} Subscription. To get more features, you can
              purchase Addon Plans.
            </Alert>
          )}
        </>
      )}

      {/* =====================================================
            PAYMENT DETAILS
        ===================================================== */}
      {showPaymentSection && (
        <Card
          ref={paymentRef}
          sx={{
            borderRadius: '16px',
            boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
            border: '1px solid #EEE',
            p: { xs: 2, sm: 3 },
            mt: 3,
            mb: 3,
          }}
        >
          <Typography sx={{ fontSize: { xs: 17, sm: 20 }, fontWeight: 700 }} gutterBottom>
            Payment Details
          </Typography>
          <Divider sx={{ mb: 2, borderColor: '#EEE' }} />

          <Box component="form" onSubmit={isIOSApp ? (e) => e.preventDefault() : handleFormSubmit}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }} gutterBottom>
              Selected {selectedAddon ? 'Addon' : 'Plan'}:
            </Typography>

            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell sx={{ padding: '6px' }}>Type</TableCell>
                  <TableCell sx={{ padding: '8px' }}>
                    {selectedAddon ? 'Addon Plan' : selectedPlan?.plan_name}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ padding: '8px' }}>User Type</TableCell>
                  <TableCell sx={{ padding: '8px' }}>
                    {selectedAddon ? selectedAddon.user_type : selectedPlan?.user_type}
                  </TableCell>
                </TableRow>

                {isSelectedFreePlan ? (
                  <TableRow>
                    <TableCell sx={{ padding: '8px', borderTop: '1px solid #e0e0e0' }}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        Amount Payable
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ padding: '8px', borderTop: '1px solid #e0e0e0', textAlign: 'right' }}>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#4caf50' }}>
                        FREE
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : isIOSApp ? (
                  (() => {
                    const appleMatch = iosPlans.find((p) => p.productId === selectedPlan?.apple_product_id);
                    const applePrice = appleMatch?.price ?? null;
                    const totalAmount = applePrice ? parseFloat(String(applePrice).replace(/[^\d.]/g, '')) : 0;
                    const basePrice = totalAmount ? (totalAmount / 1.18).toFixed(2) : '0.00';
                    const gstAmount = totalAmount ? (totalAmount - totalAmount / 1.18).toFixed(2) : '0.00';

                    return (
                      <>
                        <TableRow>
                          <TableCell sx={{ padding: '8px', color: 'text.secondary' }}>Total Amount</TableCell>
                          <TableCell sx={{ padding: '8px', textAlign: 'right' }}>₹{basePrice}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ padding: '8px', color: 'text.secondary' }}>GST (18%)</TableCell>
                          <TableCell sx={{ padding: '8px', textAlign: 'right', color: 'text.secondary' }}>
                            ₹{gstAmount}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ padding: '8px', borderTop: '1px solid #e0e0e0' }}>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              Amount Payable
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ padding: '8px', borderTop: '1px solid #e0e0e0', textAlign: 'right' }}>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: GOLD }}>
                              ₹{totalAmount.toFixed(2)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      </>
                    );
                  })()
                ) : (
                  <>
                    <TableRow>
                      <TableCell sx={{ padding: '8px', color: 'text.secondary' }}>Total Amount</TableCell>
                      <TableCell sx={{ padding: '8px', textAlign: 'right' }}>
                        ₹{selectedAddon ? selectedAddon.charges : selectedPlan?.charges}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ padding: '8px', color: 'text.secondary' }}>GST (18%)</TableCell>
                      <TableCell sx={{ padding: '8px', textAlign: 'right', color: 'text.secondary' }}>
                        ₹{gst}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ padding: '8px', borderTop: '1px solid #e0e0e0' }}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          Amount Payable
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ padding: '8px', borderTop: '1px solid #e0e0e0', textAlign: 'right' }}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          ₹{totalWithoutGST + gst}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </>
                )}
              </TableBody>
            </Table>

            <Box
              sx={{
                mt: 2,
                p: 1.5,
                backgroundColor: LIGHT_GOLD,
                borderRadius: '10px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 1.5,
              }}
            >
              <Box>
                <Typography variant="body1" sx={{ fontWeight: 700, color: isSelectedFreePlan ? '#4caf50' : 'inherit' }}>
                  {isSelectedFreePlan
                    ? 'FREE'
                    : isIOSApp
                      ? (() => {
                        const appleMatch = iosPlans.find((p) => p.productId === selectedPlan?.apple_product_id);
                        return appleMatch?.price ?? `₹${totalWithoutGST}`;
                      })()
                      : `₹${totalWithoutGST + gst}`}
                </Typography>
                <Typography variant="caption" sx={{ color: GOLD }}>
                  Amount Payable
                </Typography>
              </Box>

              {isIOSApp ? (
                <Button
                  variant="contained"
                  onClick={handleIOSPurchase}
                  disabled={!selectedPlan || isSelectedFreePlan}
                  sx={{
                    borderRadius: '25px',
                    py: 1,
                    px: 3,
                    fontWeight: 700,
                    textTransform: 'none',
                    background: GOLD,
                    '&:hover': { background: '#8f631f' },
                  }}
                >
                  Subscribe on iOS
                </Button>
              ) : (
                <Button
                  type="submit"
                  variant="contained"
                  sx={{
                    borderRadius: '25px',
                    py: 1,
                    px: 3,
                    fontWeight: 700,
                    textTransform: 'none',
                    background: GOLD,
                    '&:hover': { background: '#8f631f' },
                  }}
                >
                  {isSelectedFreePlan ? 'Activate Free Plan' : 'Complete Payment'}
                </Button>
              )}
            </Box>
          </Box>
        </Card>
      )}

      {/* =====================================================
            BUSINESS BENEFITS (decorative)
        ===================================================== */}
      <Box
        sx={{
          width: '100%',
          borderTop: '1px solid #DDD',
          borderBottom: '1px solid #DDD',
          mt: 1,
          py: { xs: 0.5, sm: 1.8, md: 2 },
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
        }}
      >
        {BENEFITS.map((item, index) => (
          <Box
            key={item.title}
            sx={{
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: { xs: 78, sm: 100, md: 110 },
              borderRight: index !== BENEFITS.length - 1 ? '1px solid #DDD' : 'none',
              px: 0.3,
            }}
          >
            <Box sx={{ color: '#8B6335', mb: { xs: 0.3, sm: 0.6 }, '& svg': { fontSize: { xs: 26, sm: 35, md: 40 } } }}>
              {item.icon}
            </Box>
            <Typography sx={{ fontSize: { xs: 10, sm: 11, md: 13 }, fontWeight: 700, textAlign: 'center', lineHeight: 1 }}>
              {item.title}
              <br />
              {item.subtitle}
            </Typography>
          </Box>
        ))}
      </Box>
      {/* =====================================================
    NEW LAUNCHING/FEATURED AND PRIME PROPERTIES
===================================================== */}
      {/* =====================================================
    NEW LAUNCHING/FEATURED AND PRIME PROPERTIES
===================================================== */}

      <Box
        sx={{
          width: "100%",
          px: { xs: 0.5, sm: 1 },
          mt: { xs: 0.5, sm: 2 },
          boxSizing: "border-box",
        }}
      >
        {/* NEW Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 1,
          }}
        >
          <Box
            sx={{
              background: "#7a3f1a",
              color: "#FFFFFF",
              fontWeight: 700,
              fontSize: "9px",
              px: 1.5,
              py: 0.3,
              borderRadius: "20px",
              letterSpacing: "0.3px",
              display: "inline-block",
              flexShrink: 0,
            }}
          >
            NEW
          </Box>
          <Typography
            sx={{
              fontSize: "13px",
              fontWeight: 700,
              color: "#222",
              lineHeight: 1.2,
            }}
          >
            New Launching/Featured and Prime properties
          </Typography>
        </Box>

        {/* Upload Card */}
        <Card
          sx={{
            p: { xs: 1, sm: 1.5 },
            borderRadius: "10px",
            border: "1px solid #E8E3DE",
            boxShadow: "none",
            background: "#FBF9F7",
          }}
        >
          <Typography
            sx={{
              fontSize: "11px",
              color: "#444",
              mb: 1.5,
              fontWeight: 500,
            }}
          >
            Upload your properties in any of these categories
          </Typography>

          {/* Single Row - All items in one line */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 0.5,
              flexWrap: "nowrap",
              overflow: "hidden",
            }}
          >
            {/* Categories in one row */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                flexShrink: 1,
                minWidth: 0,
                overflow: "hidden",
              }}
            >
              {["New Launch", "Premium", "Future", "Recommended"].map((category, index) => (
                <Box
                  key={category}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    flexShrink: 0,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "9px",
                      fontWeight: 600,
                      color: "#7a3f1a",
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                      "&:hover": {
                        color: "#7a3f1a",
                      },
                    }}
                  >
                    {category}
                  </Typography>
                  {index !== 3 && (
                    <Typography
                      sx={{
                        fontSize: "12px",
                        fontWeight: 300,
                        color: "#7a3f1a",
                      }}
                    >
                      |
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>

            {/* Upload Property Button */}
            <Button
              variant="contained"
              startIcon={<CloudUploadOutlinedIcon sx={{ fontSize: "14px" }} />}
              sx={{
                background: "#222",
                borderRadius: "20px",
                px: 1.5,
                py: 0.4,
                fontSize: "9px",
                fontWeight: 500,
                textTransform: "none",
                color: "#FFFFFF",
                whiteSpace: "nowrap",
                flexShrink: 0,
                minWidth: "auto",
                "&:hover": {
                  background: "#000",
                },
                "& .MuiButton-startIcon": {
                  marginRight: 0.5,
                },
              }}
            >
              Upload Property
            </Button>
          </Box>
        </Card>
      </Box>

      {/* =====================================================
    WHAT HAPPENS NEXT?
===================================================== */}


      <Box
        sx={{
          width: "100%",
          px: { sm: 1, md: 1.5 },
          mt: { xs: 1, sm: 4, md: 5 },
          mb: 1,
          boxSizing: "border-box",
        }}
      >
        {/* Section Title with Icon */}
        <Box
          sx={{
            display: "flex",
            alignItems: "left",
            justifyContent: "left",
            gap: { xs: 0.8, sm: 1.5 },
            mb: { xs: 1.5, sm: 2.5, md: 3 },
          }}
        >
          <Box
            sx={{
              color: GOLD,
              fontSize: { xs: 16, sm: 24, md: 28 },
              display: "flex",
              alignItems: "center",
            }}
          >
            <span role="img" aria-label="megaphone">📢</span>
          </Box>
          <Typography
            sx={{
              fontSize: { xs: 14, sm: 20, md: 24 },
              fontWeight: 700,
              color: "#222",
              textAlign: "center",
              letterSpacing: "0.3px",
            }}
          >
            What Happens Next?
          </Typography>
        </Box>

        {/* Steps Container */}
        <Box
          sx={{
            width: "100%",
            px: { xs: 0.5, sm: 2, md: 3 },
            boxSizing: "border-box",
          }}
        >
          {/* Steps in a single row */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "flex-start",
              width: "100%",
              position: "relative",
              gap: { xs: 0.5, sm: 1 },
            }}
          >
            {/* Dotted connector lines */}
            <Box
              sx={{
                position: "absolute",
                top: { xs: 14, sm: 20, md: 22 },
                left: { xs: "8%", sm: "10%", md: "8%" },
                right: { xs: "8%", sm: "10%", md: "8%" },
                height: "2px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                pointerEvents: "none",
                zIndex: 0,
              }}
            >
              {[1, 2, 3].map((item) => (
                <Box
                  key={item}
                  sx={{
                    width: { xs: "18%", sm: "22%", md: "25%" },
                    borderTop: `1.5px dashed ${GOLD}`,
                    opacity: 0.4,
                  }}
                />
              ))}
            </Box>

            {/* Step 1 - Upload Property */}
            <Box
              sx={{
                textAlign: "center",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                position: "relative",
                zIndex: 1,
                flex: 1,
                minWidth: 0,
                px: { xs: 0.5, sm: 0 },
              }}
            >
              <Box
                sx={{
                  width: { xs: 28, sm: 40, md: 44 },
                  height: { xs: 28, sm: 40, md: 44 },
                  borderRadius: "50%",
                  background: "#FFFFFF",
                  border: `2px solid ${GOLD}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: { xs: 0.5, sm: 1, md: 1.2 },
                  color: GOLD,
                  position: "relative",
                  zIndex: 2,
                }}
              >
                <CloudUploadOutlinedIcon sx={{ fontSize: { xs: 14, sm: 18, md: 20 } }} />
              </Box>

              <Typography
                sx={{
                  fontSize: { xs: 10, sm: 14, md: 16 },
                  fontWeight: 700,
                  color: "#222",
                  lineHeight: 1.2,
                  mb: 0.2,
                }}
              >
                1. Upload
                <br />
                Property
              </Typography>

              <Typography
                sx={{
                  fontSize: { xs: 9, sm: 11, md: 13 },
                  color: "#777",
                  lineHeight: 1.3,
                  maxWidth: "100%",
                  px: { xs: 0.5, sm: 0 },
                  mt: { xs: 0.3, sm: 0.5 },
                }}
              >
                Fill in property
                <br />
                details and submit.
              </Typography>
            </Box>

            {/* Step 2 - Admin Review */}
            <Box
              sx={{
                textAlign: "center",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                position: "relative",
                zIndex: 1,
                flex: 1,
                minWidth: 0,
                px: { xs: 0.5, sm: 0 },
              }}
            >
              <Box
                sx={{
                  width: { xs: 28, sm: 40, md: 44 },
                  height: { xs: 28, sm: 40, md: 44 },
                  borderRadius: "50%",
                  background: "#FFFFFF",
                  border: `2px solid ${GOLD}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: { xs: 0.5, sm: 1, md: 1.2 },
                  color: GOLD,
                  position: "relative",
                  zIndex: 2,
                }}
              >
                <SearchOutlinedIcon sx={{ fontSize: { xs: 14, sm: 18, md: 20 } }} />
              </Box>

              <Typography
                sx={{
                  fontSize: { xs: 10, sm: 14, md: 16 },
                  fontWeight: 700,
                  color: "#222",
                  lineHeight: 1.2,
                  mb: 0.2,
                }}
              >
                2. Admin
                <br />
                Review
              </Typography>

              <Typography
                sx={{
                  fontSize: { xs: 9, sm: 11, md: 13 },
                  color: "#777",
                  lineHeight: 1.3,
                  maxWidth: "100%",
                  px: { xs: 0.5, sm: 0 },
                  mt: { xs: 0.3, sm: 0.5 },
                }}
              >
                Our admin team
                <br />
                will review your
                <br />
                property.
              </Typography>
            </Box>

            {/* Step 3 - Admin Contact */}
            <Box
              sx={{
                textAlign: "center",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                position: "relative",
                zIndex: 1,
                flex: 1,
                minWidth: 0,
                px: { xs: 0.5, sm: 0 },
              }}
            >
              <Box
                sx={{
                  width: { xs: 28, sm: 40, md: 44 },
                  height: { xs: 28, sm: 40, md: 44 },
                  borderRadius: "50%",
                  background: "#FFFFFF",
                  border: `2px solid ${GOLD}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: { xs: 0.5, sm: 1, md: 1.2 },
                  color: GOLD,
                  position: "relative",
                  zIndex: 2,
                }}
              >
                <PhoneOutlinedIcon sx={{ fontSize: { xs: 14, sm: 18, md: 20 } }} />
              </Box>

              <Typography
                sx={{
                  fontSize: { xs: 10, sm: 14, md: 16 },
                  fontWeight: 700,
                  color: "#222",
                  lineHeight: 1.2,
                  mb: 0.2,
                }}
              >
                3. Admin
                <br />
                Contact
              </Typography>

              <Typography
                sx={{
                  fontSize: { xs: 9, sm: 11, md: 13 },
                  color: "#777",
                  lineHeight: 1.3,
                  maxWidth: "100%",
                  px: { xs: 0.5, sm: 0 },
                  mt: { xs: 0.3, sm: 0.5 },
                }}
              >
                Admin will
                <br />
                contact you for
                <br />
                verification.
              </Typography>
            </Box>

            {/* Step 4 - Listing Live */}
            <Box
              sx={{
                textAlign: "center",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                position: "relative",
                zIndex: 1,
                flex: 1,
                minWidth: 0,
                px: { xs: 0.5, sm: 0 },
              }}
            >
              <Box
                sx={{
                  width: { xs: 28, sm: 40, md: 44 },
                  height: { xs: 28, sm: 40, md: 44 },
                  borderRadius: "50%",
                  background: "#FFFFFF",
                  border: `2px solid ${GOLD}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: { xs: 0.5, sm: 1, md: 1.2 },
                  color: GOLD,
                  position: "relative",
                  zIndex: 2,
                }}
              >
                <CheckCircleOutlinedIcon sx={{ fontSize: { xs: 14, sm: 18, md: 20 } }} />
              </Box>

              <Typography
                sx={{
                  fontSize: { xs: 10, sm: 14, md: 16 },
                  fontWeight: 700,
                  color: "#222",
                  lineHeight: 1.2,
                  mb: 0.2,
                }}
              >
                4. Listing
                <br />
                Live
              </Typography>

              <Typography
                sx={{
                  fontSize: { xs: 9, sm: 11, md: 13 },
                  color: "#777",
                  lineHeight: 1.3,
                  maxWidth: "100%",
                  px: { xs: 0.5, sm: 0 },
                  mt: { xs: 0.3, sm: 0.5 },
                }}
              >
                Once approved,
                <br />
                your property
                <br />
                goes live.
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>


      {/* =====================================================
            RENEWAL MESSAGE
        ===================================================== */}
      <Box
        sx={{
          width: '100%',
          background: '#F8F2EB',
          py: { xs: 1, sm: 1.8 },
          px: { xs: 1, sm: 2 },
          boxSizing: 'border-box',
          textAlign: 'center',
          borderRadius: { xs: '0 0 12px 12px', sm: '0 0 12px 12px' },
        }}
      >
        {/* <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center', gap: 0.7 }}>
          <SecurityOutlinedIcon sx={{ color: '#8B6335', fontSize: { xs: 22, sm: 27 }, flexShrink: 0 }} />
          <Typography sx={{ fontSize: { xs: 10, sm: 13 }, fontWeight: 500, lineHeight: 1.4, textAlign: 'left' }}>
            Subscriptions automatically renew unless cancelled at least 24 hours before the end of the current
            period. You can manage or cancel your subscription in your Apple ID Account Settings at any time.
          </Typography>
        </Box> */}

        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: { xs: 1, sm: 2 }, mt: 1 }}>
          <Typography
            component="a"
            href="/privacypolicy"
            sx={{ fontSize: { xs: 10, sm: 13 }, fontWeight: 600, textDecoration: 'underline', color: GOLD }}
          >
            Privacy Policy
          </Typography>
          <Typography sx={{ fontSize: { xs: 10, sm: 13 } }}>•</Typography>
          <Typography
            component="a"
            href="/termsandconditions"
            sx={{ fontSize: { xs: 10, sm: 13 }, fontWeight: 600, textDecoration: 'underline', color: GOLD }}
          >
            Terms of Use (EULA)
          </Typography>
        </Box>
      </Box>

      <GuestLoginModal open={showGuestModal} onClose={() => setShowGuestModal(false)} />
    </Container>
  );
};

export default SubscriptionForm;